<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import { listNotifications } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["notifications", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? listNotifications(selectedProjectId.value)
      : Promise.resolve([]),
  enabled: () => !!selectedProjectId.value,
  refetchOnWindowFocus: true,
});

function when(iso: string | null): string {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}

const APPROVER_LABEL: Record<string, string> = {
  cre: "CRE",
  project_manager: "Project Manager",
};

</script>

<template>
  <PageLayout>
    <h1 class="text-xl font-semibold mb-1">
      Notifications
    </h1>
    <p class="text-sm text-neutral-500 mb-6">
      What needs attention on this project — items waiting on someone to act.
    </p>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="rounded-xl border border-neutral-200 bg-neutral-0 p-8 text-center text-neutral-500"
    >
      You're all caught up — nothing needs attention on this project.
    </div>
    <ul
      v-else-if="data"
      class="grid gap-2"
    >
      <li
        v-for="(item, i) in data"
        :key="i"
      >
        <RouterLink
          :to="{
            name: item.target_route,
            params: { projectId: selectedProjectId, id: item.target_id },
          }"
          class="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-0 px-4 py-3 hover:bg-neutral-50"
        >
          <span class="h-2 w-2 shrink-0 rounded-full bg-semantic-warning" />
          <span class="min-w-0 flex-1">
            <span class="block font-medium text-neutral-900">{{ item.title }}</span>
            <span
              v-if="item.detail"
              class="block truncate text-sm text-neutral-500"
            >{{ item.detail }}</span>
          </span>
          <span
            v-if="item.approver_role"
            class="shrink-0 rounded-full bg-brand-accent/15 px-2 py-0.5 text-xs font-medium text-brand-accent"
          >for {{ APPROVER_LABEL[item.approver_role] ?? item.approver_role }}</span>
          <time class="shrink-0 text-xs text-neutral-400">{{ when(item.since) }}</time>
          <svg
            class="h-4 w-4 shrink-0 text-neutral-300"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </RouterLink>
      </li>
    </ul>
  </PageLayout>
</template>
